import panzoom from 'panzoom';
import type {AssetGroup, PaginationSettings, Pageable, Asset} from '@deviceinsight/ng-ui-api-client';
import {searchAssets} from '@deviceinsight/ng-ui-api-client';
import {nanoid10} from '@deviceinsight/ng-ui-components';
import type {AssetWithMetaData} from '../types';
import {SVG_STRIP_REGEXP} from './consts';
import {WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';

const setUniqueIdsInSvg = (svgCode: string): string => {
	const suffix = `--${nanoid10()}`;
	const parser = new DOMParser();
	const document = parser.parseFromString(svgCode, 'image/svg+xml');
	const elementsWithIds = document.querySelectorAll('[id]');
	// first we need to update all ids
	elementsWithIds.forEach((element) => {
		const id = element.getAttribute('id') || '';
		const newId = `${id}${suffix}`;
		element.setAttribute('id', newId);
	});
	// then we might have <use> tags in the svg that reference the id's modified above so we need to update those links (xlink:href) also
	const elementsWithLink = document.querySelectorAll('use[*|href]:not([href])');
	elementsWithLink.forEach((element) => {
		const href = element.getAttribute('xlink:href') || '';
		const newHref = `${href}${suffix}`;
		element.setAttribute('xlink:href', newHref);
	});
	const documentContentString = new XMLSerializer().serializeToString(document);
	return documentContentString;
};

const transformBlobUrlToSvgString = async (blobUrl: string): Promise<string> => {
	const response = await fetch(blobUrl);
	const blob = await response.blob();
	return await new Promise((resolve, reject) => {
		const fileReader = new FileReader();

		fileReader.onload = () => {
			const completeResult = fileReader.result ? fileReader.result.toString() : '';
			// we need to strip here to the pure SVG Tag since the uploaded SVG may contain XML declarations etc. which break the code.
			const res = completeResult.match(SVG_STRIP_REGEXP);

			if (res && res.length) {
				// res[0] contains full match (<svg ...>...</svg>)
				const completeSvg = res[0];
				resolve(setUniqueIdsInSvg(completeSvg));
			} else {
				reject('Error parsing SVG');
			}
		};

		fileReader.onerror = reject;
		fileReader.readAsText(blob);
	});
};

const getAssetGroupFromContext = (context: WidgetProps['context']): AssetGroup | null | undefined => {
	return context.type === 'assetGroup' ? context.assetGroup : undefined;
};

const loadGroupDevices = (
	query: string,
	assetGroup: AssetGroup | null | undefined,
	availableDevices: Array<AssetWithMetaData>,
	paginationSettings: PaginationSettings,
	onlyIds?: Array<number>
): Promise<Pageable<Asset>> => {
	if (assetGroup) {
		const excludeIds = availableDevices.map((availableDevice) => availableDevice.asset.id);
		const excludeQuery = excludeIds.length ? `NOT id:(${excludeIds.join(' OR ')})` : '';
		const includeQuery = onlyIds && onlyIds.length ? `id: (${onlyIds.join(' OR ')})` : '';
		const {id: assetGroupId} = assetGroup;
		const additionalQuery = includeQuery || excludeQuery;
		return searchAssets(`${query} ${additionalQuery}  transitiveAssetGroupId:${assetGroupId}`, paginationSettings);
	} else {
		return Promise.reject('Not in asset group context');
	}
};

const addPanzoomHandlers = (container: HTMLElement | null | undefined) => {
	if (container) {
		const panzoomInstance = panzoom(container, {
			maxZoom: 4,
			minZoom: 1,
			zoomDoubleClickSpeed: 1
		});
		panzoomInstance.on('transform', () => {
			const transformObject = panzoomInstance.getTransform();
			const panzoom_wrapper = container.parentNode;

			if (panzoom_wrapper) {
				const panzoom_el = container;
				const panzoom_el_width = panzoom_el.offsetWidth * transformObject.scale;
				const panzoom_el_height = panzoom_el.offsetHeight * transformObject.scale;
				const max_x = container.offsetWidth - panzoom_el_width;
				const max_y = container.offsetHeight - panzoom_el_height;

				if (transformObject.x > 0) {
					panzoomInstance.moveTo(0, transformObject.y);
				} else if (transformObject.x < max_x) {
					// @ts-expect-error offsetWidth exists
					if (panzoom_el_width <= panzoom_wrapper.offsetWidth) {
						panzoomInstance.moveTo(0, transformObject.y);
					} else {
						panzoomInstance.moveTo(max_x, transformObject.y);
					}
				}

				if (transformObject.y > 0) {
					panzoomInstance.moveTo(transformObject.x, 0);
				} else if (transformObject.y < max_y) {
					// @ts-expect-error offsetHeight exists
					if (panzoom_el_height <= panzoom_wrapper.offsetHeight) {
						panzoomInstance.moveTo(transformObject.x, 0);
					} else {
						panzoomInstance.moveTo(transformObject.x, max_y);
					}
				}
			}
		});
	}
};

export {loadGroupDevices, getAssetGroupFromContext, transformBlobUrlToSvgString, addPanzoomHandlers};
